import { QueryClient } from "react-query";

/**
 * Global `retry` predicate: never retry a 4xx. Client errors are
 * deterministic — resending the identical request fails identically, so
 * retrying only delays the error state and spams the backend with requests
 * it will deny again (403 authz denials, 404s, 422s). The axios interceptors
 * already replay the genuinely recoverable cases (CSRF refresh, 401 token
 * refresh) before react-query ever sees the error. 408 (request timeout) and
 * 429 (rate limit) are the transient 4xx exceptions; they keep the default
 * 3-attempt budget, as do 5xx and network errors.
 *
 * Individual queries can still override `retry` per-hook.
 */
export const retryUnlessClientError = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  ) {
    return false;
  }
  return failureCount < 3;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: retryUnlessClientError,
    },
  },
});

/**
 * react-query `retry` predicate that fails fast on a 404. A 404 is
 * deterministic ("not found"), so retrying only delays the error behind
 * react-query's exponential backoff. Transient errors keep the default
 * 3-attempt budget.
 *
 * Pass as `retry: shouldNotRetry404` on a `useQuery` whose endpoint 404s
 * when the resource doesn't exist.
 */
export const shouldNotRetry404 = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 404) return false;
  return failureCount < 3;
};

/**
 * react-query `retry` predicate that fails fast on a 403 or 404. Both are
 * deterministic (access denied / not found); retrying only burns time behind
 * react-query's exponential backoff. Transient errors keep the default
 * 3-attempt budget.
 *
 * Use when the endpoint returns 403 for unauthorized users (e.g. portfolio
 * endpoints that require dataset:read) or 404 when a resource doesn't exist.
 */
export const shouldNotRetry403Or404 = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 403 || status === 404) return false;
  return failureCount < 3;
};
